import React from 'react';
import { isEqual, get, filter } from 'lodash';
import PropTypes from 'prop-types';
import { Button, Row, Col, Form, InputGroup, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import turf from '@turf/turf';

import * as videoOps from '../../../api/Videos/video-operations';
import { YOUTUBE_URL_REGEX } from '../../../api/Videos/Videos';

import ColBreak from '../../atoms/ColBreak';
import CompanyLogo from '../../atoms/CompanyLogo';
import AIAnnotationsModal from '../AIAnnotationsModal';
import AnnotationMarker from '../AnnotationMarker';
import ResponsivePlayer from '../ResponsivePlayer';
import Slider from '../Slider';
import VideoFeatureButtons from '../VideoFeatureButtons';
import VideoLatLngGo from '../VideoLatLngGo';
import VideoOptionForm from '../VideoOptionForm';
import VideoSpeedControls from '../VideoSpeedControls';
import AnnotationContainer from '../../containers/AnnotationContainer';
import MapContainerViewVideo from '../../containers/MapContainerViewVideo';

import Styles from './styles';

const ATTRIBUTES = {
  preload: 'auto'
};
const PLAYER_VARS = {
  autoplay: 0,
  controls: 0,
  disablekb: 1,
  enablejsapi: 1,
  fs: 0,
  iv_load_policy: 3,
  loop: 1,
  modestbranding: 1,
  rel: 0,
  showinfo: 0
};

const LeftComponent = ({
  video,
  annotationVisible,
  annotations,
  currentFeature,
  deployEnv,
  selectedAnnotation,
  showAllAnnotations,
  showVideo,
  updateContinuouslyMapLocation,
  updateMapLocation,
  videoLines,
  yaw,
  changeShowAllAnnotations,
  changeVideoContainerStateProperty,
  fetchTime,
  initializeLoop,
  setReferenceInVideoContainer,
  updateVideoStyle,
  ...rest
}) => {
  const annotationsToShow = filter(
    annotations,
    annotation => showAllAnnotations || isEqual(annotation, selectedAnnotation)
  );

  const rpaBearing = videoOps.isStartPosition(video) || videoOps.isDevicePosition(video) ? yaw : 0;

  const rpaPosition =
    videoOps.isStartPosition(video) || videoOps.isDevicePosition(video) ? currentFeature : {};

  const GPSCP = turf.getCoord(currentFeature);
  const coordinates = get(currentFeature, 'properties.coordinates', [GPSCP, GPSCP, GPSCP, GPSCP]);
  const videoBox = turf.polygon([
    [
      [coordinates[0][0], coordinates[0][1]],
      [coordinates[1][0], coordinates[1][1]],
      [coordinates[2][0], coordinates[2][1]],
      [coordinates[3][0], coordinates[3][1]],
      [coordinates[0][0], coordinates[0][1]]
    ]
  ]);

  const showVideoBox =
    videoOps.isCoveredSurface(video) ||
    (videoOps.isProjectedVideo(video) && !annotationVisible && !showVideo);

  const annotationProps = {
    annotations: annotationsToShow,
    annotation: selectedAnnotation,
    annotationTemplates: videoOps.getAnnotationTemplates(video),
    videoId: videoOps.getId(video),
    changeVideoContainerStateProperty,
    ...rest
  };

  const mapProps = {
    video,
    annotations: annotationsToShow,
    currentFeature,
    currentRPAPosition: rpaPosition,
    deployEnv,
    lines: videoLines,
    rpaBearing,
    showVideoBox,
    updateContinuouslyMapLocation,
    updateMapLocation,
    videoBox,
    changeVideoContainerStateProperty,
    fetchTime,
    updateVideoStyle,
    initializeLoop,
    setReferenceInVideoContainer
  };

  if (annotationVisible) {
    return <AnnotationContainer {...annotationProps} />;
  }

  return (
    <>
      <MapContainerViewVideo {...mapProps} />
      <Styles.LeafletButtons>
        <ButtonGroup vertical size="sm">
          <div
            onClick={changeShowAllAnnotations}
            title="Show All Anotations"
            role="button"
            onKeyPress={() => {}}
            tabIndex={0}
          >
            <img
              src="/maps-buttons/etiquetas.svg"
              style={{ height: '34px', width: '34px', cursor: 'pointer' }}
              alt={<FontAwesomeIcon icon="map-marker-alt" size="lg" />}
            />
          </div>
          <div
            onClick={() =>
              changeVideoContainerStateProperty(
                'updateContinuouslyMapLocation',
                !updateContinuouslyMapLocation
              )
            }
            title="Continuously Update Map Location"
            role="button"
            onKeyPress={() => {}}
            tabIndex={0}
          >
            <img
              src="/maps-buttons/centrar.svg"
              style={{ height: '34px', width: '34px', cursor: 'pointer' }}
              alt={<FontAwesomeIcon icon="map-marker-alt" size="lg" />}
            />
          </div>
        </ButtonGroup>
      </Styles.LeafletButtons>
    </>
  );
};

LeftComponent.propTypes = {
  video: PropTypes.object.isRequired,
  annotationVisible: PropTypes.bool.isRequired,
  annotations: PropTypes.array.isRequired,
  currentFeature: PropTypes.object.isRequired,
  deployEnv: PropTypes.string.isRequired,
  selectedAnnotation: PropTypes.object.isRequired,
  showAllAnnotations: PropTypes.bool.isRequired,
  showVideo: PropTypes.bool.isRequired,
  updateContinuouslyMapLocation: PropTypes.bool.isRequired,
  updateMapLocation: PropTypes.bool.isRequired,
  videoLines: PropTypes.array.isRequired,
  yaw: PropTypes.number.isRequired,
  changeShowAllAnnotations: PropTypes.func.isRequired,
  changeVideoContainerStateProperty: PropTypes.func.isRequired,
  fetchTime: PropTypes.func.isRequired,
  initializeLoop: PropTypes.func.isRequired,
  setReferenceInVideoContainer: PropTypes.func.isRequired,
  updateVideoStyle: PropTypes.func.isRequired
};

const VideoVisualizer = ({
  video,
  isClickableDivHidden,
  isPublic,
  isFree,
  speed,
  videoHeight,
  videoWidth,
  changeVideoContainerStateProperty,
  handleClickOnVideo,
  hideShowClickableDiv,
  onVideoReady,
  setReferenceInVideoContainer,
  ...rest
}) => {
  const showUnlockButton = videoOps.getURL(video).match(YOUTUBE_URL_REGEX);
  return (
    <>
      <AIAnnotationsModal
        video={video}
        changeVideoContainerStateProperty={changeVideoContainerStateProperty}
        {...rest}
      />
      <Row>
        <Col xs={12} sm={6}>
          <LeftComponent
            video={video}
            isPublic={isPublic}
            changeVideoContainerStateProperty={changeVideoContainerStateProperty}
            setReferenceInVideoContainer={setReferenceInVideoContainer}
            {...rest}
          />
          <Styles.TxtVideoContainer
            ref={node => setReferenceInVideoContainer('videoYouTubeDiv', node)}
            width={videoWidth}
            height={videoHeight}
            option={videoOps.getOption(video)}
          >
            <ResponsivePlayer
              attributes={ATTRIBUTES}
              playbackRate={speed}
              playerVars={PLAYER_VARS}
              url={videoOps.getURL(video)}
              onVideoReadyFunction={event => onVideoReady(event, 'txtPlayer')}
            />
            <Styles.DivOnVideo
              isTxtVideo
              ref={node => setReferenceInVideoContainer('nonClickableTxtDiv', node)}
            />
          </Styles.TxtVideoContainer>
        </Col>
        <Col xs={12} sm={6}>
          <Row>
            <Col xs={12}>
              <ResponsivePlayer
                attributes={ATTRIBUTES}
                playbackRate={speed}
                playerVars={PLAYER_VARS}
                url={videoOps.getURL(video)}
                onVideoReadyFunction={event => onVideoReady(event, 'player')}
              />
              <Styles.DivOnVideo
                ref={node => setReferenceInVideoContainer('clickableDiv', node)}
                onClick={handleClickOnVideo}
              >
                <AnnotationMarker {...rest} />
              </Styles.DivOnVideo>
            </Col>
          </Row>
          <ColBreak />
          <Slider
            isVideoVisualizer
            changeVideoContainerStateProperty={changeVideoContainerStateProperty}
            {...rest}
          />
          <Form id="videoButtons">
            <InputGroup size="sm">
              <VideoSpeedControls
                isClickableDivHidden={isClickableDivHidden}
                speed={speed}
                changeVideoContainerStateProperty={changeVideoContainerStateProperty}
                setReferenceInVideoContainer={setReferenceInVideoContainer}
                {...rest}
              />
              <VideoOptionForm video={video} isPublic={isPublic} {...rest} />
            </InputGroup>
            <ColBreak />
            <VideoFeatureButtons
              isClickableDivHidden={isClickableDivHidden}
              isPublic={isPublic}
              isFree={isFree}
              changeVideoContainerStateProperty={changeVideoContainerStateProperty}
              video={video}
              {...rest}
            />
            <ColBreak />
            <VideoLatLngGo
              isClickableDivHidden={isClickableDivHidden}
              isPublic={isPublic}
              {...rest}
            />
            <ColBreak />
          </Form>
          {showUnlockButton && (
            <Button
              size="sm"
              variant="info"
              active={isClickableDivHidden}
              onClick={hideShowClickableDiv}
            >
              {isClickableDivHidden ? 'Lock Carousel' : 'Unlock Carousel'}
            </Button>
          )}
          <CompanyLogo showLogo={isPublic} alignRight {...rest} />
        </Col>
      </Row>
    </>
  );
};

VideoVisualizer.propTypes = {
  video: PropTypes.object.isRequired,
  isClickableDivHidden: PropTypes.bool.isRequired,
  isPublic: PropTypes.bool.isRequired,
  isFree: PropTypes.bool.isRequired,
  speed: PropTypes.number.isRequired,
  videoHeight: PropTypes.string.isRequired,
  videoWidth: PropTypes.string.isRequired,
  changeVideoContainerStateProperty: PropTypes.func.isRequired,
  handleClickOnVideo: PropTypes.func.isRequired,
  hideShowClickableDiv: PropTypes.func.isRequired,
  onVideoReady: PropTypes.func.isRequired,
  setReferenceInVideoContainer: PropTypes.func.isRequired
};

export default VideoVisualizer;
